import React, { useEffect, useState } from 'react';
import { spanLabel } from '../../utils/workdays';
import { apiFetch } from '../../services/apiClient';
import JobPoTable, { shipInfo } from './JobPoTable';

// ─────────────────────────────────────────────────────────────────────────────
// A pillow is a list of the actual work items, the way a board list is: the holes
// on a drilling card, the tickets on USA Digs, the change orders, the POs.
//
// This file is only the LIST — what each item looks like as a row and where its
// items come from. Opening one still hands off to the board's own card, so the
// place you do the work is the same everywhere.
// ─────────────────────────────────────────────────────────────────────────────

const money = n => (n == null || n === '') ? '' : '$' + Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 });
const shortDate = s => s ? new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';

// status → a dot colour, so a list reads at a glance without a legend.
const TONE = {
  complete: '#16a34a', done: '#16a34a', received: '#16a34a', approved: '#16a34a',
  scheduled: '#2563eb', active: '#2563eb', submitted: '#2563eb',
  on_hold: '#b45309', hold: '#b45309', draft: '#9ca3af', pending: '#9ca3af',
  not_started: '#9ca3af', open: '#c2410c',
};
const toneFor = s => TONE[String(s || '').toLowerCase().replace(/\s+/g, '_')] || '#9ca3af';

// USA tickets carry their own colour code across the app — called in, active,
// expiring soon, expired — and it's the point of the list: an expired ticket means
// the crew can't dig. Same values as USA_STATUS_META uses on the board.
const USA_TONE = {
  not_called_in: '#9ca3af', called_in: '#f59e0b', active: '#22c55e', expiring_soon: '#f59e0b', expired: '#ef4444', dug: '#a8a29e',
};
const USA_WORD = {
  not_called_in: 'Not Called In', called_in: 'Called In', active: 'Active', expiring_soon: 'Expiring Soon', expired: 'Expired', dug: 'Dug',
};
const daysLeft = iso => {
  if (!iso) return null;
  const d = Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
  return Number.isFinite(d) ? d : null;
};

// Per pillow: where its items come from, what one item reads as, and what to call
// them. `path` takes the sub-job id (or the project id where the items hang off the
// job rather than a workflow card).
// The drilling card as it reads on the board: the job's drill status and comment,
// with its rig, date and hole progress as badges.
function drillingCard(holes, sj) {
  const list = Array.isArray(holes) ? holes : [];
  // 'done' is the status the server counts as finished (see getHoleCounts) — I had
  // this as 'complete', which read 0/7 on a card the drilling modal showed as 1/7.
  const done = list.filter(h => String(h.status || '').toLowerCase() === 'done').length;
  return {
    title: sj?.name || 'Drilling',
    status: sj?.status,
    sub: sj?.drill_comment || '',
    meta: [
      sj?.drill_rig && `🔧 ${sj.drill_rig}`,
      sj?.drill_date && `📅 ${spanLabel(sj.drill_date, sj.sched_days)}`,
      list.length ? `🕳️ ${done}/${list.length} holes` : 'no holes yet',
    ].filter(Boolean),
  };
}

// The Miscellaneous / Custom card as a row: its title, what it's about, and how much
// has been filed against it.
function noteCard(counts, sj) {
  return {
    title: sj?.name || 'Note',
    status: sj?.status,
    sub: sj?.notes || 'Nothing written down yet',
    meta: [
      counts.photos ? `📷 ${counts.photos}` : null,
      counts.files ? `📄 ${counts.files}` : null,
      (!counts.photos && !counts.files) ? 'no photos or documents' : null,
    ].filter(Boolean),
  };
}

export const ITEM_SOURCES = {
  // Drilling is ONE card per job on the board — the holes are a detail inside it, not
  // the cards. Listing a row per hole broke the job's drilling into seven pieces and
  // stopped looking like the board at all. The holes are fetched, but only to say
  // "3/7 holes" on the single card.
  // A job can carry more than one drilling card — two areas drilled separately, each
  // with its own rig, date and holes. So one row per CARD, and the holes are fetched
  // per card only to count them.
  vertical_drilling: {
    noun: 'card', perSubJob: true, path: sj => `/sub-jobs/${sj}/holes`,
    toItem: (holes, sj) => drillingCard(holes, sj),
    add: { label: 'drilling card', asks: 'What should this drilling card be called?' },
  },
  horizontal_drilling: {
    noun: 'card', perSubJob: true, path: sj => `/sub-jobs/${sj}/holes`,
    toItem: (holes, sj) => drillingCard(holes, sj),
    add: { label: 'drilling card', asks: 'What should this drilling card be called?' },
  },
  // Miscellaneous is the fixed catch-all; Custom is the same card under a name you
  // choose. One card each, so the pillow shows the card rather than a list.
  misc: {
    noun: 'card', perSubJob: true, opensCard: 'note',
    path: sj => `/sub-jobs/${sj}/photos`,
    toItem: (photos, sj) => noteCard({ photos: (photos || []).length }, sj),
    add: { label: 'note card', asks: 'What is this one about?' },
  },
  custom: {
    noun: 'card', perSubJob: true, opensCard: 'note',
    path: sj => `/sub-jobs/${sj}/photos`,
    toItem: (photos, sj) => noteCard({ photos: (photos || []).length }, sj),
    add: { label: 'card', asks: 'What should this card be called?' },
  },
  usa_digs: {
    noun: 'ticket', bySubJob: true, path: sj => `/sub-jobs/${sj}/usa-digs`,
    add: { label: 'ticket', asks: 'USA ticket number?', field: 'usa_number', post: sj => `/sub-jobs/${sj}/usa-digs` },
    row: d => {
      const left = daysLeft(d.expires_at);
      return {
        title: d.usa_number ? `USA ${d.usa_number}` : 'Dig ticket',
        sub: d.location || 'No location yet',
        status: d.status,
        statusLabel: USA_WORD[d.status] || d.status,
        tone: USA_TONE[d.status],
        meta: [
          d.called_in_at && `📞 ${shortDate(d.called_in_at)}`,
          // Days left is the number that decides whether you can dig tomorrow.
          d.expires_at && (left > 0 ? `⏳ ${left} day${left === 1 ? '' : 's'} left` : `⛔ expired ${shortDate(d.expires_at)}`),
          d.ticket_pdf_filename && '📄 ticket',
          (d.lat && d.lng) && '📍 located',
        ].filter(Boolean),
        note: d.notes || '',
      };
    },
  },
  loops: {
    noun: 'location', bySubJob: true, path: sj => `/sub-jobs/${sj}/loop-locations`,
    add: { label: 'location', asks: 'Which street or intersection?', field: 'street1', post: sj => `/sub-jobs/${sj}/loop-locations` },
    row: l => ({ title: (l.street1 || 'Loop location').split(',')[0], sub: l.street2 || '',
      meta: [l.qty_gate && `${l.qty_gate} gate`, l.qty_thru && `${l.qty_thru} thru`, l.qty_left_turn && `${l.qty_left_turn} left`, l.qty_advance && `${l.qty_advance} advance`, l.gate_size && `size ${l.gate_size}`].filter(Boolean),
      note: l.directions || '' }),
  },
  nve: {
    noun: 'meter', bySubJob: true, path: sj => `/sub-jobs/${sj}/nve-meters`,
    add: { label: 'meter', asks: 'Meter address?', field: 'address', post: sj => `/sub-jobs/${sj}/nve-meters` },
    row: m => ({
      title: m.address || 'Meter',
      status: m.status,
      sub: [m.service_size, m.amps && `${m.amps}A`, m.voltage].filter(Boolean).join(' · '),
      meta: [
        m.permit_number && `📄 ${m.permit_number}`,
        m.permit_date && `permit ${shortDate(m.permit_date)}`,
        // The city sign-off is the thing that holds an NVE meter up, so it reads
        // either way round rather than only when it has happened.
        m.city_ok ? `✅ city OK${m.city_ok_date ? ' ' + shortDate(m.city_ok_date) : ''}` : '⏳ awaiting city',
      ].filter(Boolean),
    }),
  },
  shop: {
    noun: 'move', path: id => `/equipment-movements?projectId=${id}`,
    row: m => ({
      title: m.equipment_name || 'Equipment',
      status: m.status,
      sub: `${m.from_label || '—'} → ${m.to_label || '—'}`,
      meta: [
        m.moved_at && `📅 ${shortDate(m.moved_at)}`,
        m.equipment_category,
        m.driver && `👤 ${m.driver}`,
      ].filter(Boolean),
      note: m.notes || '',
    }),
  },
  submittals: {
    noun: 'submittal', bySubJob: true, path: sj => `/sub-jobs/${sj}/submittals`,
    row: s => ({ title: s.category || s.titan_number || 'Submittal', status: s.status,
      sub: s.ball_in_court ? `with ${s.ball_in_court}` : '',
      meta: [s.titan_number && `#${s.titan_number}`, s.revision > 1 && `Rev ${s.revision}`, s.spec_number && `spec ${s.spec_number}`, s.released_date && `released ${shortDate(s.released_date)}`].filter(Boolean) }),
  },
  extra_work: {
    // Extra work is EW001, EW002… until it's promoted to a Change Order, when it takes
    // its CO number — these aren't change orders yet, so they don't read CO.
    noun: 'ticket', bySubJob: true, path: sj => `/sub-jobs/${sj}/extra-work`,
    row: t => { const code = t.promoted_at ? `CO-${t.co_number}` : `EW${String(t.co_number).padStart(3, '0')}`;
      return { title: t.title || code, status: t.status,
      sub: t.reason || t.description || '',
      meta: [code, t.work_date && `📅 ${shortDate(t.work_date)}`, t.locked_at && '🔒 printed'].filter(Boolean) }; },
  },
  change_orders: {
    noun: 'change order', bySubJob: true, path: sj => `/sub-jobs/${sj}/extra-work`,
    row: t => ({ title: t.title || `CO-${String(t.co_number).padStart(2, '0')}`, status: t.status,
      sub: t.reason || t.description || '',
      meta: [`CO-${String(t.co_number).padStart(2, '0')}`, t.work_date && `📅 ${shortDate(t.work_date)}`, t.locked_at && '🔒 printed'].filter(Boolean) }),
  },
  purchase_orders: {
    noun: 'PO', path: id => `/projects/${id}/purchase-orders`,
    // PO, vendor, who wrote it, amount, status, then the description — Ryan's order.
    // Rendered as columns rather than the generic row, so they line up down the list.
    columns: true,
    sorts: [
      { key: 'po',     label: 'PO number', get: p => Number(p.po_number) || 0, dir: -1 },
      // Major POs to the top (Pat, Sep 2026) — soonest ship date first among them.
      { key: 'major',  label: 'Major first', get: p => `${p.is_major ? 0 : 1}|${String(p.ship_date || '9999').slice(0, 10)}` },
      { key: 'ship',   label: 'Ship date', get: p => String(p.ship_date || '9999').slice(0, 10) },
      { key: 'vendor', label: 'Vendor',    get: p => String(p.vendor || '').toLowerCase() },
      { key: 'who',    label: 'Written by', get: p => String(p.created_by_name || '').toLowerCase() },
      { key: 'cost',   label: 'Amount',    get: p => Number(p.cost) || 0, dir: -1 },
      { key: 'status', label: 'Status',    get: p => String(p.status || '').toLowerCase() },
    ],
    row: p => ({
      po: `#${p.po_number}`,
      vendor: p.vendor || '—',
      who: p.created_by_name || '—',
      amount: p.cost != null && p.cost !== '' ? money(p.cost) : '—',
      status: p.status,
      description: p.description || '',
      rental: !!p.is_rental,
      major: !!p.is_major,
      ship: p.ship_date ? shipInfo(p) : null,
    }),
  },
  punch_list: {
    noun: 'item', path: id => `/projects/${id}/punch-list`,
    add: { label: 'item', asks: 'What needs finishing?', field: 'description', post: id => `/projects/${id}/punch-list` },
    row: i => ({ title: i.description || 'Item', status: i.status,
      meta: [i.assigned_to && `👤 ${i.assigned_to}`].filter(Boolean) }),
  },
  close_out: {
    noun: 'item', path: id => `/projects/${id}/closeout`,
    row: i => ({ title: i.description || 'Item', sub: i.checked_by || '', status: i.checked ? 'complete' : 'open' }),
  },
  daily_updates: {
    noun: 'update', path: id => `/projects/${id}/daily-updates`,
    row: u => ({ title: u.author_name || 'Someone', sub: u.notes || '', meta: [shortDate(u.created_at)].filter(Boolean) }),
  },
  work_orders: {
    noun: 'work order', path: id => `/projects/${id}/work-orders`,
    // Goes through createWorkOrderUnder — numbers it 2633-WO001, puts it on Small
    // Jobs and gives it its own board card and bid shell, exactly as today.
    add: { label: 'work order', asks: 'What is the work order for?', field: 'name', post: id => `/projects/${id}/work-orders` },
    row: w => ({ title: w.project_number || 'WO', sub: w.name || '', status: w.wo_status || w.status,
      meta: [w.client_name && `🏢 ${w.client_name}`, w.sched_finish && `📅 ${shortDate(w.sched_finish)}`].filter(Boolean) }),
  },
};

// Load a pillow's items. Returns null while loading so a count can hold its place
// rather than flashing "0 cards" and then correcting itself.
export function usePillowItems(pillowKey, { projectId, subJobId, subJobs = [], reloadKey = 0 }) {
  const src = ITEM_SOURCES[pillowKey];
  const [items, setItems] = useState(null);
  const key = src ? (src.bySubJob ? subJobId : projectId) : null;
  // For a per-card source there is one fetch PER card, so the identity that matters
  // is the set of card ids, not a single one.
  const ids = subJobs.map(x => x.id).join(',');

  useEffect(() => {
    if (!src) { setItems(null); return undefined; }
    let live = true;

    // Drilling has one row per CARD, each needing its own holes counted.
    if (src.perSubJob) {
      if (!subJobs.length) { setItems([]); return undefined; }
      Promise.all(subJobs.map(sj =>
        apiFetch(src.path(sj.id)).catch(() => []).then(rows => src.toItem(Array.isArray(rows) ? rows : [], sj))
      )).then(list => { if (live) setItems(list); });
      return () => { live = false; };
    }

    if (!key) { setItems([]); return undefined; }
    apiFetch(src.path(key))
      .then(r => { if (live) setItems(Array.isArray(r) ? r : []); })
      .catch(() => { if (live) setItems([]); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, key, ids, reloadKey]);

  return { items, source: src };
}

// Add one item to a pillow. Returns the message to show; throws with the server's own
// wording when it refuses, rather than a generic failure.
export async function addPillowItem(source, { projectId, subJobId, value }) {
  const add = source?.add;
  if (!add || !add.post) throw new Error('Nothing to add here');
  const target = source.bySubJob ? subJobId : projectId;
  await apiFetch(add.post(target), {
    method: 'POST',
    body: JSON.stringify({ [add.field]: value }),
  });
  return `${add.label.charAt(0).toUpperCase()}${add.label.slice(1)} added.`;
}

export function countLabel(n, noun) {
  if (n == null) return '';
  // An NVE job with no meters is underground only (Ryan, Oct 3 2026) — say that, not "No meters".
  if (n === 0 && noun === 'meter') return 'Underground only · no meters';
  if (n === 0) return `No ${noun}s`;
  return `${n} ${noun}${n === 1 ? '' : 's'}`;
}

const badge = { fontSize: 10.5, color: '#6b7280', background: '#f3f4f6', padding: '2px 7px', borderRadius: 10, fontWeight: 600, whiteSpace: 'nowrap' };

// A PO reads as columns, not as a card: number, vendor, who wrote it, amount, status,
// then the description underneath. Six things on one line is unreadable in a narrow
// pillow, so the description wraps below and the rest stay in order across the top.
export function PoRow({ data, canSeeMoney = true, onOpen }) {
  const [hover, setHover] = useState(false);
  const tone = toneFor(data.status);
  const cell = { fontSize: 12, color: '#374151', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
  return (
    <button onClick={onOpen}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{ display: 'block', width: '100%', textAlign: 'left', font: 'inherit', cursor: 'pointer',
        background: hover ? '#fafafa' : '#fff', border: '1px solid ' + (hover ? '#e5e7eb' : '#ececec'),
        borderLeft: `4px solid ${tone}`, borderRadius: 10, padding: '9px 11px', marginBottom: 8 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, fontWeight: 800, color: '#c2410c', whiteSpace: 'nowrap' }}>{data.po}</span>
        {data.major && <span title="Major PO" style={{ fontSize: 9, fontWeight: 800, color: '#b91c1c', background: '#fee2e2', border: '1px solid #fca5a5', padding: '1px 5px', borderRadius: 5, whiteSpace: 'nowrap' }}>⭐ MAJOR</span>}
        <span style={{ ...cell, flex: '1 1 90px', fontWeight: 600, color: '#111' }}>{data.vendor}</span>
        <span style={{ ...cell, flex: '1 1 80px', color: '#6b7280' }}>{data.who}</span>
        {/* Zone decides whether a pillow is shown; the money inside it is its own check. */}
        {canSeeMoney && <span style={{ fontSize: 12.5, fontWeight: 700, color: '#111', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{data.amount}</span>}
        {data.status && (
          <span style={{ fontSize: 10, fontWeight: 700, borderRadius: 20, padding: '2px 8px',
            background: tone + '1f', color: tone, whiteSpace: 'nowrap' }}>{data.status}</span>
        )}
        {data.rental && <span style={{ fontSize: 10, fontWeight: 700, color: '#6b7280' }}>🔑</span>}
      </div>
      {data.ship && (
        <div style={{ fontSize: 11.5, marginTop: 4, color: data.ship.tone === '#111' ? '#374151' : data.ship.tone, fontWeight: data.ship.bold ? 800 : 600 }}>
          🚚 Ships {data.ship.text}{data.ship.sub ? ` · ${data.ship.sub}` : ''}
        </div>
      )}
      {data.description && (
        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4, lineHeight: 1.35,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {data.description}
        </div>
      )}
    </button>
  );
}

// Sort a pillow's items by one of its declared sorts.
export function sortItems(rows, sorts, key, flip) {
  const s = (sorts || []).find(x => x.key === key);
  if (!s || !Array.isArray(rows)) return rows;
  const dir = (s.dir || 1) * (flip ? -1 : 1);
  return [...rows].sort((a, b) => {
    const x = s.get(a), y = s.get(b);
    return (x < y ? -1 : x > y ? 1 : 0) * dir;
  });
}

// One item, built like the cards on the boards themselves: a status stripe down the
// left, the name and its status, a line of detail and a row of badges. The thin
// two-line row this replaced didn't carry enough to be worth clicking.
export function ItemRow({ data, onOpen }) {
  const [hover, setHover] = useState(false);
  const tone = data.tone || toneFor(data.status);
  const label = data.statusLabel || String(data.status || '').replace(/_/g, ' ');
  return (
    <button
      onClick={onOpen}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'block', width: '100%', textAlign: 'left', font: 'inherit', cursor: 'pointer',
        background: hover ? '#fafafa' : '#fff',
        border: '1px solid ' + (hover ? '#e5e7eb' : '#ececec'),
        borderLeft: `4px solid ${tone}`, borderRadius: 10,
        padding: '10px 12px', marginBottom: 8,
      }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 800, color: '#111',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {data.title || '—'}
        </span>
        {data.status !== undefined && label && (
          <span style={{ fontSize: 10, fontWeight: 700, borderRadius: 20, padding: '2px 8px',
            background: tone + '1f', color: tone, whiteSpace: 'nowrap', textTransform: 'capitalize' }}>
            {label}
          </span>
        )}
      </div>
      {data.sub && (
        <div style={{ fontSize: 12, color: '#374151', marginTop: 3, lineHeight: 1.35,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {data.sub}
        </div>
      )}
      {!!(data.meta && data.meta.length) && (
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 7 }}>
          {data.meta.map((m, i) => <span key={i} style={badge}>{m}</span>)}
        </div>
      )}
      {data.note && (
        <div style={{ fontSize: 11.5, color: '#6b7280', marginTop: 6, lineHeight: 1.35,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {data.note}
        </div>
      )}
    </button>
  );
}

// Whether a pillow keeps a list of its own — POs, tickets, meters, punch items.
export function hasItemSource(pillowKey) {
  return !!ITEM_SOURCES[pillowKey];
}

// The same list the pillow shows in its window, opened out.
//
// The expanded view used to fall through to a different component, which meant the
// PO list lost the column order and the sorting the moment you clicked it open —
// more room, less information. One list, rendered twice, can't drift like that.
// With the room, it also gets a search box.
export function PillowItemList(props) {
  // POs get the whole table — every column the Administration page has, a ship date,
  // and the Major POs pulled to the front. Everything else keeps the generic list.
  if (props.pillowKey === 'purchase_orders') return <JobPoTable projectId={props.projectId} canSeeMoney={props.canSeeMoney !== false} openItemId={props.openItemId || null} />;
  return <GenericItemList {...props} />;
}

function GenericItemList({ pillowKey, projectId, subJobId, subJobs = [], canSeeMoney = true, onOpen }) {
  const [sortKey, setSortKey] = useState(null);
  const [flip, setFlip] = useState(false);
  const [q, setQ] = useState('');
  const { items: rows, source } = usePillowItems(pillowKey, { projectId, subJobId, subJobs });

  if (!source) return null;
  if (rows == null) return <div style={{ fontSize: 13, color: '#9ca3af' }}>Loading…</div>;

  const sorted = source.sorts && sortKey ? sortItems(rows, source.sorts, sortKey, flip) : rows;
  const needle = q.trim().toLowerCase();
  const mapped = sorted.map((it, i) => ({ it, i, data: source.row ? source.row(it) : it }));
  // Search across whatever the row actually shows, so what you type matches what
  // you can see — the PO number, the vendor, who wrote it, the description.
  const shown = needle
    ? mapped.filter(({ data }) => Object.values(data).some(v => typeof v === 'string' && v.toLowerCase().includes(needle)))
    : mapped;

  const sel = { fontSize: 12, padding: '5px 8px', borderRadius: 7, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
        <input type="text" value={q} onChange={e => setQ(e.target.value)}
          placeholder={`Search ${countLabel(rows.length, source.noun)}…`}
          style={{ ...sel, cursor: 'text', flex: '1 1 220px', minWidth: 160 }} />
        {source.sorts && (
          <select value={sortKey || ''} style={sel}
            onChange={e => { const v = e.target.value; if (v === sortKey) setFlip(f => !f); else { setSortKey(v || null); setFlip(false); } }}>
            <option value="">Sort…</option>
            {source.sorts.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        )}
        {sortKey && (
          <button onClick={() => setFlip(f => !f)} title="Reverse the order"
            style={{ ...sel, fontWeight: 700, color: '#c2410c' }}>{flip ? '↑' : '↓'}</button>
        )}
        <span style={{ fontSize: 12, color: '#9ca3af', marginLeft: 'auto' }}>
          {needle ? `${shown.length} of ${rows.length}` : countLabel(rows.length, source.noun)}
        </span>
      </div>

      {shown.length === 0 ? (
        <div style={{ fontSize: 13, color: '#9ca3af', padding: '10px 2px' }}>
          {needle ? 'Nothing matches that.' : `No ${source.noun}s on this job.`}
        </div>
      ) : shown.map(({ it, i, data }) => (
        source.columns
          ? <PoRow key={it.id || i} data={data} canSeeMoney={canSeeMoney} onOpen={() => onOpen && onOpen(it, i)} />
          : <ItemRow key={it.id || i} data={data} onOpen={() => onOpen && onOpen(it, i)} />
      ))}
    </div>
  );
}
